/**
 * Zod Schemas for ETL Integration Response Validation
 *
 * These schemas are used to validate responses from the etl_integrations service
 * at runtime, and to derive TypeScript types via z.infer.
 */
import { z } from 'zod';

const EtlStatusDetailsSchema = z.object({
  // Known status types from AirAmbulanceForm constants (STATUS_TYPES), with z.string() fallback for unknown values
  type: z
    .union([
      z.literal('accepted'),
      z.literal('dispatchReceived'),
      z.literal('dispatchReceivedStandby'),
      z.literal('dispatchReviewing'),
      z.literal('checkingWeather'),
      z.literal('preparingToLaunch'),
      z.literal('enRouteToScene'),
      z.literal('arrivedToScene'),
      z.literal('transporting'),
      z.literal('arrivedToDropoff'),
      z.literal('complete'),
      z.literal('pendingCancellation'),
      z.literal('cancelled'),
      z.literal('dispatchDelayed'),
      z.literal('error'),
      z.literal('cancelPending'),
      z.literal('cancelDisregarded'),
      z.literal('cancelCompleted'),
      z.literal('editLocationPending'),
      z.literal('editLocationDisregarded'),
      z.literal('editLocationCompleted'),
      z.literal('activationRequested'),
      z.literal('checkingWeatherStandby'),
      z.literal('dispatchReviewingStandby'),
      z.literal('waitingForActivation'),
      z.literal('activationPending'),
      z.literal('activationCompleted'),
      z.string(),
    ])
    .nullable()
    .optional(),
  display: z.string().nullable().optional(),
  statusTimeUTC: z.string().nullable().optional(),
});

const EtlVehicleDetailsSchema = z.object({
  id: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
});
export type EtlVehicleDetails = z.infer<typeof EtlVehicleDetailsSchema>;

const EtlProviderDetailsSchema = z.object({
  name: z.string().nullable().optional(),
});
export type EtlProviderDetails = z.infer<typeof EtlProviderDetailsSchema>;

const EtlUrlSchema = z.object({
  transportTracking: z.string().nullable().optional(),
});

const EtlEtaDetailsSchema = z.object({
  display: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  utcTime: z.string().nullable().optional(),
  timeOffset: z.string().nullable().optional(),
});
export type EtlEtaDetails = z.infer<typeof EtlEtaDetailsSchema>;

const EtlDispatchCenterDetailsSchema = z.object({
  name: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
});
export type EtlDispatchCenterDetails = z.infer<
  typeof EtlDispatchCenterDetailsSchema
>;

const EtlUpdateDetailsSchema = z.object({
  status: EtlStatusDetailsSchema.nullable().optional(),
  eta: z.array(EtlEtaDetailsSchema).nullable().optional(),
  vehicle: EtlVehicleDetailsSchema.nullable().optional(),
  provider: EtlProviderDetailsSchema.nullable().optional(),
  dispatchCenter: EtlDispatchCenterDetailsSchema.nullable().optional(),
});

const EtlStatusVerbatimMessageSchema = z.object({
  update: EtlUpdateDetailsSchema.nullable().optional(),
  url: EtlUrlSchema.nullable().optional(),
});
export type EtlStatusVerbatimMessage = z.infer<
  typeof EtlStatusVerbatimMessageSchema
>;

const EtlStatusResponseSchema = z.object({
  id: z.number(),
  gmr_trip_id: z.string(),
  created_at: z.string(),
  ingestion_source: z.string().nullish(),
  verbatim_message: EtlStatusVerbatimMessageSchema.nullable().optional(),
});
export type EtlStatusResponse = z.infer<typeof EtlStatusResponseSchema>;

const EtlTransportResponseSchema = z.object({
  id: z.number(),
  gmr_transport_id: z.string(),
  account_id: z.string(),
  created_at: z.string(),
  is_test: z.boolean(),
  ingestion_source: z.string().nullish(),
  verbatim_message: z.record(z.unknown()).nullable().optional(),
  statuses: z.array(EtlStatusResponseSchema),
  caller_id: z.string().optional().nullable(),
});
export type EtlTransportResponse = z.infer<typeof EtlTransportResponseSchema>;

const EtlTransportTentativeEtaSchema = z.object({
  eta: z.string(),
  providers: z.string(),
});

const EtlErrorEnvelopeSchema = z.object({
  status: z.literal('failure'),
  detail: z.string(),
});

const EtlSuccessEnvelopeSchema = z.object({
  status: z.literal('success'),
});

/**
 * Create an envelope schema that discriminates on `status`.
 * Success: { status: "success", result: <resultSchema> }
 * Failure: { status: "failure", detail: string }
 */
function etlEnvelope<T extends z.ZodTypeAny>(resultSchema: T) {
  return z.discriminatedUnion('status', [
    z.object({ ...EtlSuccessEnvelopeSchema.shape, result: resultSchema }),
    EtlErrorEnvelopeSchema,
  ]);
}

// POST /api/gmr/transports response
export const EtlCreateTransportResponseSchema = etlEnvelope(
  EtlTransportResponseSchema,
);
export type EtlCreateTransportResponse = z.infer<
  typeof EtlCreateTransportResponseSchema
>;
/** This is @see EtlCreateTransportResponse.result when @see EtlCreateTransportResponse.status == "success" */
export type EtlCreateTransportResult = Extract<
  EtlCreateTransportResponse,
  { status: 'success' }
>['result'];

// GET /api/gmr/transports response
export const EtlListTransportsResponseSchema = etlEnvelope(
  z.object({ transports: z.array(EtlTransportResponseSchema) }),
);
export type EtlListTransportsResponse = z.infer<
  typeof EtlListTransportsResponseSchema
>;
export type EtlListTransportsResult = Extract<
  EtlListTransportsResponse,
  { status: 'success' }
>['result'];

// GET /api/gmr/transports/eta response
export const EtlGetTransportTentativeEtaResponseSchema = etlEnvelope(
  EtlTransportTentativeEtaSchema,
);
export type EtlGetTransportTentativeEtaResponse = z.infer<
  typeof EtlGetTransportTentativeEtaResponseSchema
>;
export type EtlGetTransportTentativeEtaResult = Extract<
  EtlGetTransportTentativeEtaResponse,
  { status: 'success' }
>['result'];

// Transport Update Request Schema (PATCH /api/gmr/transports/:id)
export const EtlApiTransportUpdateRequestSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  landingZoneId: z.string().nullable().optional(),
  tripReason: z.string().optional(),
  comments: z.string().optional(),
  pickupLocationComments: z.string().optional(),
  multipleUnites: z.boolean().optional(),
  otherResponders: z.boolean().optional(),
  otherRespondersDeclinedWeather: z.boolean().optional(),
  groundContact: z.string().optional(),
  callerAddress: z.string().optional(),
});
export type EtlApiTransportUpdateRequest = z.infer<
  typeof EtlApiTransportUpdateRequestSchema
>;

// Transport Cancel Request Schema (DELETE /api/gmr/transports/:id)
export const EtlApiTransportCancelRequestSchema = z.object({
  reason: z.string(),
});
export type EtlApiTransportCancelRequest = z.infer<
  typeof EtlApiTransportCancelRequestSchema
>;

// Transport Request Schema (POST /api/gmr/transports)
export const EtlApiTransportRequestSchema = z.object({
  isTest: z.boolean(),
  isStandby: z.boolean(),
  tripReason: z.string().optional(),
  comments: z.string().optional(),
  callerId: z.string().optional(),
  multipleUnites: z.boolean().optional(),
  latitude: z.number(),
  longitude: z.number(),
  landingZoneId: z.string().optional(),
  pickupLocationComments: z.string().optional(),
  otherResponders: z.boolean(),
  otherRespondersDeclinedWeather: z.boolean(),
  groundContact: z.string().optional(),
  callerAddress: z.string().optional(),
});
export type EtlApiTransportRequest = z.infer<
  typeof EtlApiTransportRequestSchema
>;

const TransportVerbatimMessageSchema = z.object({
  tripReason: z.string().nullable().optional(),
  comments: z.string().nullable().optional(),
  caller: z
    .object({
      address: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  tripLeg: z
    .array(
      z.object({
        multipleUnits: z.boolean().nullable().optional(),
        pickupLocation: z
          .object({
            locationName: z.string().nullable().optional(),
            address1: z.string().nullable().optional(),
            city: z.string().nullable().optional(),
            state: z.string().nullable().optional(),
            zip: z.string().nullable().optional(),
            latitude: z.number().nullable().optional(),
            longitude: z.number().nullable().optional(),
            comment: z.string().nullable().optional(),
          })
          .nullable()
          .optional(),
        dropoffLocation: z
          .object({
            locationName: z.string().nullable().optional(),
            address1: z.string().nullable().optional(),
            city: z.string().nullable().optional(),
            state: z.string().nullable().optional(),
            zip: z.string().nullable().optional(),
          })
          .nullable()
          .optional(),
        airDetails: z
          .object({
            idenStandBy: z.boolean().nullable().optional(),
            svcWeatherDecline: z.boolean().nullable().optional(),
            svcOtherRespond: z.boolean().nullable().optional(),
            groundContact: z.string().nullable().optional(),
          })
          .nullable()
          .optional(),
      }),
    )
    .nullable()
    .optional(),
});
export type TransportVerbatimMessage = z.infer<
  typeof TransportVerbatimMessageSchema
>;

// Query string parameters for fetching transport tentative eta
export interface EtlTransportTentativeEtaParams {
  latitude: number;
  longitude: number;
}

/**
 * Parse a transport's untyped verbatim_message into a typed object.
 * Returns undefined if the input is missing or fails validation.
 */
function parseTransportVerbatimMessage(
  verbatimMessage: Record<string, unknown> | null | undefined,
): TransportVerbatimMessage | undefined {
  if (!verbatimMessage) return undefined;
  const parsed = TransportVerbatimMessageSchema.safeParse(verbatimMessage);
  if (!parsed.success) {
    // eslint-disable-next-line no-console -- unexpected: verbatim_message failed schema validation. .error logs to sentry
    console.error(
      'parseTransportVerbatimMessage: failed to parse verbatim_message',
      parsed.error,
    );
    return undefined;
  }
  return parsed.data;
}

/**
 * Shared shape for verbatim trip leg locations, both pickup and dropoff).
 * Both locations carry the same structured address fields.
 */
export type TripLocation = {
  locationName?: string | null;
  address1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
};

/**
 * Format a trip leg location, either pickup or dropoff, into a single display string.
 * Returns null if no meaningful parts are present.
 */
export function formatTripLocationAddress(
  location: TripLocation | null | undefined,
): string | null {
  if (!location) return null;

  const cityStateZip = [location.city, location.state, location.zip]
    .filter(Boolean)
    .join(' ');

  const locationInfo = [
    location.locationName,
    location.address1,
    cityStateZip,
  ].filter(Boolean);

  if (locationInfo.length === 0) return null;

  return locationInfo.join(', ');
}

/**
 * Extract the pickup address from an already-parsed verbatim message.
 * Prefers tripLeg[0].pickupLocation structured fields over caller.address.
 * Returns null when neither is present.
 */
function extractTransportAddressFromVerbatim(
  verbatim: TransportVerbatimMessage,
): string | null {
  const tripPickupAddress = formatTripLocationAddress(
    verbatim.tripLeg?.[0]?.pickupLocation,
  );

  if (tripPickupAddress) return tripPickupAddress;

  const callerAddress = verbatim.caller?.address;
  if (!callerAddress) return null;

  return callerAddress;
}

// TODO: elevate pickup_address to a top-level column in etl_integrations so callers read transport.pickup_address directly instead of parsing verbatim_message
/**
 * Extract the pickup address from a transport's untyped verbatim_message.
 * Parses the message, then delegates to {@link extractTransportAddressFromVerbatim}.
 * Returns null when the message is missing, invalid, or contains no address.
 */
function extractTransportAddress(
  verbatimMessage: Record<string, unknown> | null | undefined,
): string | null {
  const parsed = parseTransportVerbatimMessage(verbatimMessage);
  if (!parsed) {
    // eslint-disable-next-line no-console -- unexpected: transports should always have verbatim_message
    console.warn(
      'extractTransportAddress: verbatim_message is undefined or invalid',
    );
    return null;
  }

  const address = extractTransportAddressFromVerbatim(parsed);
  if (address) return address;

  // eslint-disable-next-line no-console -- unexpected: neither address field was present
  console.warn(
    'extractTransportAddress: no address found in tripLeg[0].pickupLocation or caller.address',
  );

  return null;
}

/** Display labels for GMR transport `ingestion_source` values from the API. */
export const INGESTION_SOURCE_LABELS = {
  unite: 'UNITE',
  gmr: 'transport.net',
  midas: 'Phone',
} as const satisfies Record<string, string>;

type IngestionSource = keyof typeof INGESTION_SOURCE_LABELS;

function hasIngestionSourceLabel(source: string): source is IngestionSource {
  return Object.prototype.hasOwnProperty.call(INGESTION_SOURCE_LABELS, source);
}

/**
 * Map `ingestion_source` to a user-facing label.
 * Returns `"Unknown"` when missing or unrecognized.
 */
function formatIngestionSource(source: string | null | undefined): string {
  return source && hasIngestionSourceLabel(source)
    ? INGESTION_SOURCE_LABELS[source]
    : 'Unknown';
}

export {
  parseTransportVerbatimMessage,
  extractTransportAddress,
  extractTransportAddressFromVerbatim,
  formatIngestionSource,
};

/**
 * Status display item derived from ETL response for UI rendering
 */
export interface StatusDisplayItem {
  type?: string;
  display?: string;
  timestamp?: string;
  vehicle?: EtlVehicleDetails;
  provider?: EtlProviderDetails;
  dispatchCenter?: EtlDispatchCenterDetails;
  trackingUrl?: string;
  eta?: EtlEtaDetails[];
}
